import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Calendar, CheckCircle, Clock, MessageSquare } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const BookACall = () => {
  const callBenefits = [
    {
      icon: MessageSquare,
      title: 'No Pitch, No Pressure',
      description: 'A genuine conversation about where you are and what you need'
    },
    {
      icon: CheckCircle,
      title: 'Clear Next Steps',
      description: 'Walk away knowing exactly what to do next, whether you work with me or not'
    },
    {
      icon: Clock,
      title: 'Your Time is Valued',
      description: '30 minutes focused entirely on your situation and goals'
    }
  ];

  const whatToExpect = [
    "Where you are right now in your job search",
    "What's been holding you back",
    "Which roles you should be targeting",
    "Whether coaching is right for you",
    "Which package would actually help (if any)"
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Book a Free Discovery Call | Mattoo Consulting</title>
        <meta name="description" content="Schedule a free 30-minute discovery call. No pitch, no pressure — just an honest conversation about your career goals." />
      </Helmet>

      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-brand-mist/50 to-brand-pale/30">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <h1 className="font-serif text-5xl md:text-6xl font-bold tracking-tight text-brand-dark mb-6">
              Book a Free Discovery Call
            </h1>
            <p className="text-xl text-muted-foreground">
              Ready to take the next step in your career? Let's connect.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <span className="inline-block text-sm font-medium tracking-wide uppercase text-brand-medium mb-4">
                What Happens on the Call
              </span>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight text-brand-dark mb-6">
                An Honest Conversation About Your Career
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                A free 30-minute call. No pitch, no pressure — just an honest look at where you are, what's holding you back, and whether (and which) package would actually help.
              </p>
              <div className="space-y-4">
                <p className="text-brand-dark font-medium mb-4">We'll discuss:</p>
                {whatToExpect.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-brand-mist rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-brand-primary" />
                    </div>
                    <p className="text-muted-foreground">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-brand-cream p-8 rounded-3xl border border-brand-pale/50">
                <div className="grid gap-6">
                  {callBenefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0">
                        <benefit.icon className="w-6 h-6 text-brand-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-brand-dark mb-1">
                          {benefit.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {benefit.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Calendly Section */}
      <section className="py-20 md:py-32 bg-brand-mist/30">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-primary rounded-2xl mb-6">
              <Calendar className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight text-brand-dark mb-4">
              Choose Your Time
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Pick a time that works for you. I'll meet you there.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-3xl shadow-xl overflow-hidden border border-brand-pale/50"
          >
            <iframe
              src="https://calendly.com/anantmattoo/free-30-minute-discovery-call"
              width="100%"
              height="700"
              frameBorder="0"
              title="Schedule a meeting with Anant Mattoo"
            />
          </motion.div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <h3 className="font-serif text-2xl md:text-3xl font-semibold text-brand-dark mb-4">
              Prefer to Email?
            </h3>
            <p className="text-muted-foreground mb-6">
              You can also reach me directly at:
            </p>
            <a
              href="mailto:info@mattooconsulting.com"
              className="inline-flex items-center gap-2 text-brand-primary hover:text-brand-medium text-lg font-medium transition-colors"
            >
              <MessageSquare className="w-5 h-5" />
              info@mattooconsulting.com
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BookACall;
